import i18n from 'i18next';

/**
 * Tell the API what language the user is reading, on every request.
 *
 * The server writes prose for people — video concepts, storyboard narration
 * that is then read aloud — and it wrote all of it in English because nothing
 * ever told it otherwise. A founder screenshot shows a Hebrew storyboard editor
 * wrapped around an English scene description and an English narration line.
 *
 * This is a global `fetch` wrapper for one unhappy reason: there is no shared
 * API client to put it in. The app has ~22 separate local `apiFetch` copies and
 * a scattering of bare `fetch` calls, so the alternative was 22 edits that the
 * 23rd caller would silently miss. Wrapping `fetch` is the only change that
 * cannot be forgotten by the next component.
 *
 * `Accept-Language` is a CORS-safelisted request header, so this adds no
 * preflight. It is still scoped to our own API: other people's servers have no
 * business being told anything by us, and a header we add to a third-party
 * request is a header we did not think through.
 */

/** Does this URL point at the Soltik API? */
function isOwnApi(url: string): boolean {
  try {
    // Relative URLs ('/api/...') are same-origin by definition.
    const u = new URL(url, window.location.href);
    if (u.origin !== window.location.origin) {
      return /(^|\.)soltik\.app$/.test(u.hostname) || /\.workers\.dev$/.test(u.hostname);
    }
    return u.pathname.startsWith('/api');
  } catch { return false; }
}

export function installLocaleHeader(): void {
  if (typeof window === 'undefined' || !window.fetch) return;
  const original = window.fetch.bind(window);

  window.fetch = function patched(input: RequestInfo | URL, init?: RequestInit) {
    try {
      const url = typeof input === 'string' ? input
        : input instanceof URL ? input.href
          : (input as Request).url;
      const lang = i18n.language;

      // Skip when there is nothing useful to say. Sending "en" explicitly would
      // also be correct, but the server already defaults to it.
      // English too. Skipping it let the BROWSER's own Accept-Language reach the
      // server, so a user in Israel reading the English site got a Hebrew
      // concept and Hebrew narration. The UI language is the truth, always.
      if (lang && isOwnApi(url)) {
        const headers = new Headers(
          init?.headers ?? (input instanceof Request ? input.headers : undefined),
        );
        // Never overwrite a caller that set this deliberately.
        if (!headers.has('Accept-Language')) headers.set('Accept-Language', lang);
        return original(input, { ...init, headers });
      }
    } catch { /* a language header must never be the reason a request fails */ }
    return original(input as any, init);
  } as typeof window.fetch;
}
